import * as React from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  PanelRightClose,
  PanelRightOpen,
  AlertOctagon,
  Clock,
  TrendingUp,
  Info,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../cn";

// The rail reads as two modes. An ACTION zone (tone="action") carries a leading glyph,
// a heavier/darker title and a tabular count chip so "what needs me, how many" is the
// first thing seen. A quieter AMBIENT/NARRATIVE zone (briefing + activity) keeps the
// faint uppercase label it has today and reads top-down as read-only. A single hairline
// (the `divider` flag) marks the boundary. All sharpness comes from hierarchy, type
// weight, the existing semantic-soft severity tokens and one rule — never a new accent
// or a resting shadow.

export type RailTone = "action" | "narrative" | "ambient";
export type RailSeverity = "bad" | "warn" | "info" | "neutral";

const COUNT_CHIP_TONE: Record<RailSeverity, string> = {
  bad: "bg-badSoft text-bad",
  warn: "bg-warnSoft text-warn",
  info: "bg-infoSoft text-info",
  neutral: "bg-subtle text-mute",
};

/** Sticky, collapsible right-rail container for quick actions / storytelling. */
export function RightRail({
  children,
  collapsed,
  onToggle,
  summary,
  className,
}: {
  children: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
  /**
   * Collapsed-strip summary. When provided, the collapsed rail shows a single leading
   * action glyph (the first entry's icon) plus ONE count badge totalling everything that
   * needs the user, tinted by the highest severity, so the strip answers "N things need
   * you" without expanding. Omit ⇒ the strip falls back to today's single expand icon.
   */
  summary?: RailSummary[];
  className?: string;
}) {
  if (collapsed) {
    return (
      <CollapsedRail onToggle={onToggle} summary={summary} />
    );
  }
  return (
    <aside className={cn("flex w-80 shrink-0 flex-col gap-4 overflow-y-auto border-l border-line bg-card p-5", className)}>
      {onToggle && (
        <div className="flex justify-end pb-1">
          <button
            aria-label="Collapse right rail"
            onClick={onToggle}
            className="rounded-md p-1.5 text-faint hover:bg-subtle hover:text-ink"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>
      )}
      {children}
    </aside>
  );
}

export type RailSummary = {
  id: string;
  /** Total things needing the user across the action zone (drives the badge number). */
  count?: number;
  /** Highest severity across the action zone — tints the badge. */
  severity?: RailSeverity;
};

const COLLAPSED_BADGE_TONE: Record<RailSeverity, string> = {
  bad: "bg-bad text-onDark",
  warn: "bg-warn text-onDark",
  info: "bg-info text-onDark",
  neutral: "bg-subtle text-mute",
};

function CollapsedRail({ onToggle, summary }: { onToggle?: () => void; summary?: RailSummary[] }) {
  // Critic edit: NOT a per-section coloured-badge stack (over-design in a 40px strip).
  // One quiet action glyph + ONE total count badge is enough to answer "N things need you".
  const total = summary?.reduce((n, s) => n + (s.count ?? 0), 0) ?? 0;
  const severity: RailSeverity =
    summary?.find((s) => s.severity === "bad")?.severity ??
    summary?.find((s) => s.severity === "warn")?.severity ??
    summary?.find((s) => s.severity === "info")?.severity ??
    "neutral";
  const showBadge = total > 0;
  return (
    <div className="flex w-10 shrink-0 flex-col items-center gap-3 border-l border-line bg-card pt-4">
      <button
        aria-label="Expand right rail"
        onClick={onToggle}
        className="rounded-md p-1.5 text-faint hover:bg-subtle hover:text-ink"
      >
        <PanelRightOpen className="h-4 w-4" />
      </button>
      {showBadge && (
        <button
          aria-label={`${total} item${total === 1 ? "" : "s"} need you — expand right rail`}
          onClick={onToggle}
          className="relative rounded-md p-1.5 text-mute hover:bg-subtle hover:text-ink"
        >
          <AlertOctagon className="h-4 w-4" />
          <span
            className={cn(
              "absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums ring-2 ring-card",
              COLLAPSED_BADGE_TONE[severity],
            )}
          >
            {total > 9 ? "9+" : total}
          </span>
        </button>
      )}
    </div>
  );
}

export function RailSection({
  title,
  tone = "narrative",
  icon: Icon,
  count,
  severity = "neutral",
  divider,
  children,
  className,
}: {
  title: string;
  /** "action" ⇒ heavier dark title + optional glyph + count chip. Else quiet faint label. */
  tone?: RailTone;
  /** Leading glyph, action tone only. */
  icon?: LucideIcon;
  /** Renders the trailing tabular count chip; omit ⇒ no chip. */
  count?: number;
  /** Tints the count chip to the highest item severity (action tone). */
  severity?: RailSeverity;
  /** Adds a top hairline + spacing to mark the action→ambient boundary. */
  divider?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const isAction = tone === "action";
  return (
    <section
      className={cn(
        "flex flex-col gap-3",
        divider && "-mt-1 border-t border-hairline pt-4",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {isAction && Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-mute" aria-hidden />}
          <h3
            className={cn(
              isAction
                ? "truncate text-[13px] font-semibold tracking-tight text-ink"
                : "text-xs font-semibold uppercase tracking-wide text-faint",
            )}
          >
            {title}
          </h3>
        </div>
        {isAction && typeof count === "number" && (
          <span
            className={cn(
              "inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
              COUNT_CHIP_TONE[severity],
            )}
          >
            {count}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

/** Optional explicit divider element, for surfaces that prefer it over RailSection's `divider`. */
export function RailDivider({ className }: { className?: string }) {
  return <div className={cn("border-t border-hairline", className)} aria-hidden />;
}

export type AttentionSeverity = "overdue" | "due_soon" | "watch" | "info";

const SEVERITY_META: Record<
  AttentionSeverity,
  { icon: LucideIcon; bg: string; fg: string; reason: string }
> = {
  // Same soft-circle vocabulary as ActivityFeed.KIND_META, so the two never drift.
  // Reasons are queue-state nouns (never colour-words, never "High/Critical" alarm words).
  overdue: { icon: AlertOctagon, bg: "bg-badSoft", fg: "text-bad", reason: "Overdue" },
  due_soon: { icon: Clock, bg: "bg-warnSoft", fg: "text-warn", reason: "Due soon" },
  watch: { icon: TrendingUp, bg: "bg-infoSoft", fg: "text-info", reason: "Watch" },
  info: { icon: Info, bg: "bg-neutralSoft", fg: "text-faint", reason: "For info" },
};

export type AttentionItem = {
  id: string;
  /** Drives the soft-circle icon, colour and the reason chip. Defaults to "info". */
  severity?: AttentionSeverity;
  /** Retained for callers/data; the displayed reason word comes from severity. */
  kind: string;
  body: string;
  figure: string;
  href: string;
  /** The workspace this item concerns, when the caller has resolved one —
   * purely data, this component doesn't act on it (no domain/store import
   * here). A caller that knows how to switch workspaces (e.g.
   * StratusDashboardLayout) can pass onClick to do so before the Link
   * underneath navigates. */
  workspaceId?: string;
  /** Fires before navigating to href — does not prevent it. */
  onClick?: () => void;
};

/**
 * Action-zone attention list. Per-severity soft-circle icon + a single queue-state reason
 * chip + an always-visible chevron so rows look tappable at rest. Critic edit: exactly ONE
 * coloured chip per row (the severity noun); the raw `kind` lives inside the body sentence.
 */
export function AttentionList({
  items,
  emptyLabel = "Nothing needs you right now.",
}: {
  items: AttentionItem[];
  emptyLabel?: string;
}) {
  if (items.length === 0) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 text-xs text-mute">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-ok" aria-hidden />
        <span>{emptyLabel}</span>
      </div>
    );
  }
  return (
    <ol className="flex flex-col gap-1">
      {items.map((item) => {
        const m = SEVERITY_META[item.severity ?? "info"];
        const Icon = m.icon;
        return (
          <li key={item.id}>
            <Link
              to={item.href}
              onClick={item.onClick}
              className="group flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-subtle"
            >
              <span
                className={cn(
                  "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                  m.bg,
                )}
              >
                <Icon className={cn("h-3.5 w-3.5", m.fg)} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className={cn("text-[11px] font-semibold uppercase tracking-wide", m.fg)}>
                  {m.reason}
                </span>
                <span className="mt-0.5 block text-sm leading-snug text-ink line-clamp-2">{item.body}</span>
              </span>
              <span className="mt-0.5 flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium tabular-nums text-mute">
                {item.figure}
                <ChevronRight className="h-3.5 w-3.5 text-faint transition-colors group-hover:text-mute" />
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}

export type ActivityItem = { id: string; text: string; time: string };

/** Render an ISO timestamp as a relative time; pass non-dates through unchanged. */
function displayTime(time: string): string {
  const d = new Date(time);
  if (Number.isNaN(d.getTime())) return time;
  return formatDistanceToNow(d, { addSuffix: true });
}

/** Recent activity list (legacy plain feed; ActivityFeed is the richer shared primitive). */
export function ActivityList({ items }: { items: ActivityItem[] }) {
  return (
    <ul className="flex flex-col gap-2.5">
      {items.map((item) => (
        <li key={item.id} className="flex flex-col gap-0.5">
          <span className="text-sm leading-snug text-mute">{item.text}</span>
          <span className="text-xs text-faint tnum">{displayTime(item.time)}</span>
        </li>
      ))}
    </ul>
  );
}
